import React, { useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Alert,
  Box,
  CircularProgress,
  Typography,
  IconButton
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import DeleteIcon from '@mui/icons-material/Delete';
import FormField from './FormField';
import useForm from './useForm';
import BillingInfoSection from '../../features/clients/components/BillingInfoSection';
import ClientSelect from './ClientSelect';

/** Αποφυγή κοινής αναφοράς στο schema.initialValues (ιδίως allocations) και race reset όταν ανοίγει το modal. */
function cloneSchemaInitialValues(schema) {
  const iv = schema?.initialValues || {};
  const cloned = { ...iv };
  if (Array.isArray(iv.allocations)) {
    cloned.allocations = iv.allocations.map((row) =>
      row && typeof row === 'object' ? { ...row } : row
    );
  } else {
    cloned.allocations = [];
  }
  return cloned;
}

function buildFormStateFromEntity(schema, entity) {
  const base = cloneSchemaInitialValues(schema);
  const clean = Object.keys(entity).reduce((acc, key) => {
    const value = entity[key];
    if (Array.isArray(value)) {
      if (key === 'allocations') {
        acc[key] = value.map((row) => (row && typeof row === 'object' ? { ...row } : row));
      } else {
        acc[key] = value;
      }
    } else {
      acc[key] = value !== null && value !== undefined ? value : '';
    }
    return acc;
  }, {});
  return { ...base, ...clean };
}

const FormBuilder = ({ 
  schema, 
  modalProps = {},
  formProps = {},
  onSubmit,
  entity = null,
  open,
  onClose,
  options = {}, // For dropdown options
  onFieldChange = null // Callback for field changes
}) => {
  // State for dynamic options
  const [dynamicOptions, setDynamicOptions] = useState({});
  const [loadingOptions, setLoadingOptions] = useState({});
  const [allocationSuggestLoading, setAllocationSuggestLoading] = useState(false);
  const [allocationSuggestHint, setAllocationSuggestHint] = useState(null);
  const loadingFlagsRef = useRef({}); // Ref to track loading state without causing re-renders
   // Initialize the useForm hook
  const {
    formData,
    errors,
    loading,
    success,
    handleChange,
    handleSubmit: submitForm,
    setFormData,
    setSuccess,
    validate
  } = useForm({
    initialValues: schema.initialValues || {},
    validationRules: schema.validationRules || {},
    formatters: schema.formatters || {},
    onSubmit,
    ...formProps
  });

  const entitySnapshotRef = useRef(entity);
  entitySnapshotRef.current = entity;

  // Συγχρονισμός φόρμας όταν ανοίγει το modal (χωρίς race isInitialLoad → καθυστερημένο reset που έσβηνε κατανομές).
  // entity?.id ώστε να μην ξανατρέχει σε κάθε νέα αναφορά αντικειμένου από το parent.
  useEffect(() => {
    if (!open) {
      setAllocationSuggestHint(null);
      setAllocationSuggestLoading(false);
    }
  }, [open]);

  const entityBillingSignature = entity?.billing_info
    ? JSON.stringify(entity.billing_info)
    : '';

  useEffect(() => {
    if (!open) return;
    const ent = entitySnapshotRef.current;
    if (ent) {
      setFormData(buildFormStateFromEntity(schema, ent));
    } else {
      setFormData(cloneSchemaInitialValues(schema));
    }
  }, [open, entity?.id, entityBillingSignature, schema, setFormData]);

  // Track previous form data for onChange callbacks
  const prevFormDataRef = useRef(formData);
  const prevAllocationsRef = useRef(null);
  

  // Handle field-specific onChange callbacks when form data changes
  // Separate useEffect for onChange callbacks (doesn't cause re-renders)
  useEffect(() => {
    const prevFormData = prevFormDataRef.current;
    
    // Find which field changed and call onChange if exists
    Object.keys(formData).forEach(fieldName => {
      if (prevFormData[fieldName] !== formData[fieldName]) {
        // Find the field definition
        const field = schema.sections?.flatMap(section => section.fields || [])
          .find(f => f.name === fieldName);
        
        if (field && field.onChange) {
          field.onChange(fieldName, formData[fieldName], formData);
        }
      }
    });
    
    prevFormDataRef.current = formData;
  }, [formData, schema.sections]);

  // Separate useEffect for allocations reload (only runs when allocations change)
  useEffect(() => {
    const currentAllocations = formData.allocations;
    const prevAllocations = prevAllocationsRef.current;
    
    // Only reload options if allocations actually changed (deep comparison)
    const allocationsChanged = JSON.stringify(currentAllocations) !== JSON.stringify(prevAllocations);
    
    if (allocationsChanged && schema.sections) {
      // Reload immediately without debounce to avoid focus loss
      const dynamicArrayFields = schema.sections
        .flatMap(section => section.fields || [])
        .filter(f => f.type === 'dynamic-array' && f.itemSchema);
      
      dynamicArrayFields.forEach(async (arrayField) => {
        const itemFields = arrayField.itemSchema?.sections
          ?.flatMap(section => section.fields || [])
          .filter(itemField => itemField.loadOptions) || [];
        
        for (const itemField of itemFields) {
          // Use ref to track if we're already loading to avoid concurrent calls
          if (loadingFlagsRef.current[itemField.name]) {
            continue; // Skip if already loading
          }
          
          loadingFlagsRef.current[itemField.name] = true;
          setLoadingOptions(prev => ({ ...prev, [itemField.name]: true }));
          
          itemField.loadOptions(formData).then(options => {
            setDynamicOptions(prevOpts => ({ ...prevOpts, [itemField.name]: options }));
          }).catch(error => {
            console.error('Error reloading options:', error);
          }).finally(() => {
            loadingFlagsRef.current[itemField.name] = false;
            setLoadingOptions(prevOpts => ({ ...prevOpts, [itemField.name]: false }));
          });
        }
      });
    }
    
    prevAllocationsRef.current = currentAllocations;
  }, [formData.allocations, schema.sections]); // Only depend on allocations, not entire formData

  // Load dynamic options when modal opens and formData is ready
  // Use a ref to track if we've already loaded options for this modal session
  const optionsLoadedRef = useRef(false);
  
  useEffect(() => {
    // Reset when modal closes
    if (!open) {
      optionsLoadedRef.current = false;
      return;
    }
    
    // Only load if modal is open, formData exists, and we haven't loaded yet
    if (open && schema.sections && formData && Object.keys(formData).length > 0 && !optionsLoadedRef.current) {
      const fieldsWithLoadOptions = schema.sections
        .flatMap(section => section.fields || [])
        .filter(field => field.loadOptions && !field.dependsOn); // Only load fields without dependencies
      
      // Load fields that don't depend on other fields
      fieldsWithLoadOptions.forEach(async (field) => {
        if (!dynamicOptions[field.name] && !loadingFlagsRef.current[field.name]) {
          loadingFlagsRef.current[field.name] = true;
          setLoadingOptions(prev => ({ ...prev, [field.name]: true }));
          try {
            const options = field.loadOptions.length > 0 
              ? await field.loadOptions(formData)
              : await field.loadOptions();
            setDynamicOptions(prev => ({ ...prev, [field.name]: options }));
          } catch (error) {
            console.error(`Error loading options for ${field.name}:`, error);
            setDynamicOptions(prev => ({ ...prev, [field.name]: [] }));
          } finally {
            loadingFlagsRef.current[field.name] = false;
            setLoadingOptions(prev => ({ ...prev, [field.name]: false }));
          }
        }
      });
      
      optionsLoadedRef.current = true;
    }
  }, [open, schema.sections, formData]); // Include formData but check if already loaded

  // Helper: refresh dynamic options on demand without clearing current options
  const refreshDynamicOptions = async () => {
    if (!schema.sections) return;

    // Get all fields with loadOptions, including nested ones in dynamic arrays
    const fieldsWithLoadOptions = [];

    schema.sections.forEach(section => {
      section.fields?.forEach(field => {
        if (field.loadOptions) {
          fieldsWithLoadOptions.push(field);
        }

        // Check for dynamic array fields with nested loadOptions
        if (field.type === 'dynamic-array' && field.itemSchema?.sections) {
          field.itemSchema.sections.forEach(itemSection => {
            itemSection.fields?.forEach(itemField => {
              if (itemField.loadOptions) {
                fieldsWithLoadOptions.push(itemField);
              }
            });
          });
        }
      });
    });

    for (const field of fieldsWithLoadOptions) {
      setLoadingOptions(prev => ({ ...prev, [field.name]: true }));
      try {
        const options = field.loadOptions.length > 0
          ? await field.loadOptions(formData)
          : await field.loadOptions();
        setDynamicOptions(prev => ({ ...prev, [field.name]: options }));
      } catch (error) {
        console.error(`Error refreshing options for ${field.name}:`, error);
        // keep previous options if load fails
      } finally {
        setLoadingOptions(prev => ({ ...prev, [field.name]: false }));
      }
    }
  };

  // Removed duplicate useEffect that was causing infinite loops
  // Options loading is now handled by:
  // 1. Initial load when modal opens (existing useEffect at line ~142)
  // 2. When client_id changes (useEffect below)
  // 3. When allocations change (handled in onChange useEffect above with debounce)

  // Load options when client_id changes
  // This useEffect only runs when client_id changes, not on every formData change
  const prevClientIdRef = useRef(null);
  
  useEffect(() => {
    if (formData && schema.sections && open) {
      const currentClientId = formData.client_id;
      
      // Only reload if client_id actually changed
      if (currentClientId && currentClientId !== prevClientIdRef.current) {
        prevClientIdRef.current = currentClientId;
        
        // Load immediately without debounce
        const fieldsWithDependencies = schema.sections
          .flatMap(section => section.fields || [])
          .filter(field => field.dependsOn === 'client_id');
        
        fieldsWithDependencies.forEach(async (field) => {
          const isDynamicArray = field.type === 'dynamic-array';
          
          // For dynamic arrays, load options for items inside
          if (isDynamicArray && field.itemSchema) {
            const itemFields = field.itemSchema.sections
              ?.flatMap(section => section.fields || [])
              .filter(itemField => itemField.loadOptions) || [];
            
            for (const itemField of itemFields) {
              // Only load if options don't exist yet and not already loading
              if (!dynamicOptions[itemField.name]?.length && !loadingFlagsRef.current[itemField.name]) {
                loadingFlagsRef.current[itemField.name] = true;
                setLoadingOptions(prev => ({ ...prev, [itemField.name]: true }));
                try {
                  const options = await itemField.loadOptions(formData);
                  setDynamicOptions(prev => ({ ...prev, [itemField.name]: options }));
                } catch (error) {
                  setDynamicOptions(prev => ({ ...prev, [itemField.name]: [] }));
                } finally {
                  loadingFlagsRef.current[itemField.name] = false;
                  setLoadingOptions(prev => ({ ...prev, [itemField.name]: false }));
                }
              }
            }
          } else if (field.loadOptions) {
            // Handle regular fields with loadOptions
            if (!dynamicOptions[field.name]?.length && !loadingFlagsRef.current[field.name]) {
              loadingFlagsRef.current[field.name] = true;
              setLoadingOptions(prev => ({ ...prev, [field.name]: true }));
              try {
                const options = await field.loadOptions(currentClientId);
                setDynamicOptions(prev => ({ ...prev, [field.name]: options }));
              } catch (error) {
                setDynamicOptions(prev => ({ ...prev, [field.name]: [] }));
              } finally {
                loadingFlagsRef.current[field.name] = false;
                setLoadingOptions(prev => ({ ...prev, [field.name]: false }));
              }
            }
          }
        });
      }
    }
    
  }, [formData?.client_id, schema.sections, open]); // Include open to ensure we only load when modal is open

  // Call onChange for default values on initial load
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  
  useEffect(() => {
    if (formData && Object.keys(formData).length > 0 && open && !initialLoadDone) {
      // Find fields with onChange callbacks and call them with initial values
      schema.sections?.flatMap(section => section.fields || [])
        .forEach(field => {
              if (field.onChange && formData[field.name] !== undefined && formData[field.name] !== '') {
                // Call onChange for fields that have initial values
                field.onChange(formData, setFormValue);
              }
        });
      setInitialLoadDone(true);
    }
    
    // Reset when modal closes
    if (!open) {
      setInitialLoadDone(false);
      setDynamicOptions({});
      setLoadingOptions({});
      prevClientIdRef.current = null;
      prevAllocationsRef.current = null;
    }
  }, [open, formData, initialLoadDone]); // Run when modal opens and formData is set

  // Removed duplicate useEffect - allocations reload is already handled by the onChange useEffect above (line 122-181) with proper debounce

  const handleFormSubmit = async () => {
    // First validate the form
    if (!validate()) {
      console.log('Form validation failed:', errors);
      return;
    }
    
    // Clean form data before submission - filter out null/empty values from allocations
    const cleanFormData = { ...formData };
    if (cleanFormData.allocations && Array.isArray(cleanFormData.allocations)) {
      // First filter out null/empty values
      let filteredAllocations = cleanFormData.allocations.filter(alloc => {
        return alloc.service_id && 
               alloc.service_id !== '' && 
               alloc.allocated_amount && 
               alloc.allocated_amount !== '' && 
               parseFloat(alloc.allocated_amount) > 0;
      });
      
      // Then remove duplicate services (keep only the first occurrence of each service_id)
      const seenServices = new Set();
      filteredAllocations = filteredAllocations.filter(alloc => {
        const serviceId = parseInt(alloc.service_id);
        if (seenServices.has(serviceId)) {
          return false; // Skip duplicate service
        }
        seenServices.add(serviceId);
        return true;
      });
      
      // Finally map to clean data types
      cleanFormData.allocations = filteredAllocations.map(alloc => ({
        ...alloc,
        service_id: parseInt(alloc.service_id),
        allocated_amount: parseFloat(alloc.allocated_amount)
      }));
    }
    
    // Sync UI και στείλε το ίδιο snapshot στο submit (αλλιώς το handleSubmit βλέπει παλιό formData)
    setFormData(cleanFormData);
    const result = await submitForm(cleanFormData);
    if (result && result.success) {
      setSuccess(true);
      // Refresh dynamic options so labels (e.g., service paid_amount) update immediately
      // Only refresh if this is an allocation update (to update service paid_amount)
      if (schema.sections?.some(section => 
        section.fields?.some(field => 
          field.name === 'allocations' || 
          (field.type === 'dynamic-array' && field.itemSchema?.sections?.some(itemSection =>
            itemSection.fields?.some(itemField => itemField.name === 'service_id')
          ))
        )
      )) {
        await refreshDynamicOptions();
      }

      // Force re-render by updating formData slightly to trigger useEffect
      setFormData(prev => ({ ...prev, _refresh: Date.now() }));

      // Do not auto-close by default to avoid flicker; let parent decide
      // Parent pages can close on success if they wish
      setTimeout(() => setSuccess(false), 1200);
    }
  };

  const handleFieldChange = (nameOrEvent, directValue) => {
    // Call the original handleChange
    handleChange(nameOrEvent, directValue);
    
    // Call the onFieldChange callback if provided
    if (onFieldChange) {
      let name, value;
      
      if (typeof nameOrEvent === 'string') {
        name = nameOrEvent;
        value = directValue;
      } else {
        const event = nameOrEvent;
        name = event.target.name;
        value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
      }
      
      onFieldChange(name, value, formData, setFormValue, handleChange);
    }
  };

  // Helper function to set form values
  const setFormValue = (name, value) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const renderField = (field) => {
    // Check if field should be shown based on conditional logic
    if (field.conditional && field.conditional.show) {
      const shouldShow = field.conditional.show(formData);
      if (!shouldShow) {
        return null;
      }
    }
    
    // Handle dynamic array fields specially
    if (field.type === 'dynamic-array') {
      return renderDynamicArrayField(field);
    }
    
    // Handle client_select fields with ClientSelect component
    if (field.type === 'client_select') {
      const fieldOnChange = (nameOrEvent, directValue) => {
        // ClientSelect passes (name, value) or just (value)
        if (typeof nameOrEvent === 'string') {
          handleFieldChange(nameOrEvent, directValue);
        } else {
          handleFieldChange(field.name, nameOrEvent);
        }
      };
      
      return (
        <Grid item xs={12} sm={field.size || 6} key={field.name}>
          <ClientSelect
            name={field.name}
            label={field.label || 'Πελάτης'}
            value={formData[field.name] || ''}
            onChange={fieldOnChange}
            error={Boolean(errors[field.name])}
            helperText={errors[field.name]}
            required={field.required || false}
            disabled={field.disabled || false}
            placeholder={field.placeholder || 'Επιλέξτε ή αναζητήστε πελάτη...'}
            size={field.size === 12 ? 'medium' : 'medium'}
          />
        </Grid>
      );
    }
    
    // Determine field options: dynamic options first, then static options
    // Use optionsVersion to force re-render when options change
    const fieldOptions = dynamicOptions[field.name] || options[field.name] || field.options || [];
    const isLoading = loadingOptions[field.name];
    
    // Create custom onChange handler
    const fieldOnChange = (nameOrEvent, directValue) => {
      // Call the standard field change handler
      handleFieldChange(nameOrEvent, directValue);
    };
    
    // Ensure field has required properties and remove custom props
    const safeField = {
      label: field.label || field.name || 'Field',
      name: field.name || 'unnamed_field',
      type: field.type || 'text',
      required: field.required || false,
      placeholder: field.placeholder,
      disabled: field.disabled || false,
      icon: field.icon,
      multiline: field.multiline,
      rows: field.rows,
      inputProps: field.inputProps
    };
    
    return (
      <Grid item xs={12} sm={field.size || 6} key={field.name}>
        <FormField
          {...safeField}
          value={formData[field.name] || ''}
          onChange={fieldOnChange}
          error={errors[field.name]}
          options={fieldOptions}
          loading={isLoading}
        />
      </Grid>
    );
  };

  const renderDynamicArrayField = (field) => {
    const arrayValue = formData[field.name] || [];
    
    // Calculate total allocated amount for live balance
    const totalAllocated = arrayValue.reduce((sum, item) => {
      return sum + (parseFloat(item.allocated_amount) || 0);
    }, 0);
    
    // Get payment amount for live balance calculation
    const paymentAmount = parseFloat(formData.amount) || 0;
    const rawRemainingBalance = paymentAmount - totalAllocated;
    
    // FIX: Round to 2 decimals and treat very small negative numbers as zero
    const remainingBalance = Math.round(rawRemainingBalance * 100) / 100;
    const displayRemaining = Math.abs(remainingBalance) < 0.01 ? 0 : remainingBalance;
    
    const addItem = () => {
      const newItem = {};
      // Initialize with default values from itemSchema
      if (field.itemSchema && field.itemSchema.sections) {
        field.itemSchema.sections.forEach(section => {
          section.fields?.forEach(itemField => {
            if (itemField.defaultValue !== undefined) {
              newItem[itemField.name] = itemField.defaultValue;
            }
          });
        });
      }
      
      const newArray = [...arrayValue, newItem];
      handleFieldChange(field.name, newArray);
    };
    
    const removeItem = (index) => {
      const newArray = arrayValue.filter((_, i) => i !== index);
      handleFieldChange(field.name, newArray);
    };
    
    const updateItem = (index, itemData) => {
      const newArray = [...arrayValue];
      newArray[index] = { ...newArray[index], ...itemData };
      handleFieldChange(field.name, newArray);
    };
    
    // Helper function to get service info for balance calculation
    const getServiceInfo = (serviceId) => {
      const serviceOptions = dynamicOptions.service_id || [];
      const service = serviceOptions.find(opt => opt.value === parseInt(serviceId));
      return service;
    };
    
    // Check if service is fully paid based on its label
    const isServicePaid = (item) => {
      if (!item.service_id) return false;
      const service = getServiceInfo(item.service_id);
      return service && service.label && service.label.includes('✅');
    };
    
    return (
      <Grid item xs={12} key={field.name}>
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
            <Typography variant="h6" component="label">
              {field.label}
              {field.required && <span style={{ color: '#d32f2f' }}> *</span>}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {field.suggestAllocations?.fetch && field.name === 'allocations' && (
                <Button
                  variant="outlined"
                  color="secondary"
                  size="small"
                  disabled={allocationSuggestLoading}
                  onClick={async () => {
                    setAllocationSuggestHint(null);
                    setAllocationSuggestLoading(true);
                    try {
                      const result = await field.suggestAllocations.fetch(formData);
                      if (!result.ok) {
                        setAllocationSuggestHint({ severity: 'error', text: result.error });
                        return;
                      }
                      const rows = (result.allocations || []).map((a) => ({
                        service_id: String(a.service_id),
                        allocated_amount: parseFloat(a.allocated_amount) || 0,
                        notes: a.notes || '',
                      }));
                      handleFieldChange(field.name, rows);
                      await refreshDynamicOptions();
                      const lo = Number(result.leftoverUnallocated) || 0;
                      const loTxt =
                        lo > 0.01
                          ? ` Μένουν ${lo.toFixed(2)} € χωρίς γραμμή κατανομής (υπόλοιπο κατάθεσης).`
                          : '';
                      setAllocationSuggestHint({
                        severity: 'success',
                        text:
                          rows.length === 0
                            ? `Δεν βρέθηκε ανοιχτό υπόλοιπο υπηρεσίας για κατανομή.${loTxt}`
                            : `Προστέθηκαν ${rows.length} γραμμή/ές.${loTxt}`,
                      });
                    } catch (e) {
                      console.error(e);
                      setAllocationSuggestHint({
                        severity: 'error',
                        text: e.message || 'Σφάλμα πρότασης κατανομής',
                      });
                    } finally {
                      setAllocationSuggestLoading(false);
                    }
                  }}
                  startIcon={
                    allocationSuggestLoading ? (
                      <CircularProgress color="inherit" size={16} />
                    ) : (
                      <AutoAwesomeIcon />
                    )
                  }
                >
                  Πρόταση
                </Button>
              )}
              <Button
                variant="outlined"
                size="small"
                onClick={addItem}
                startIcon={<AddIcon />}
              >
                Προσθήκη
              </Button>
            </Box>
          </Box>
          
          {field.description && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {field.description}
            </Typography>
          )}

          {field.name === 'allocations' && allocationSuggestHint && (
            <Alert
              severity={allocationSuggestHint.severity}
              sx={{ mb: 2 }}
              onClose={() => setAllocationSuggestHint(null)}
            >
              {allocationSuggestHint.text}
            </Alert>
          )}
          
          {/* Display validation errors for the allocations array */}
          {errors[field.name] && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="error" sx={{ 
                p: 1, 
                backgroundColor: 'error.50', 
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'error.200'
              }}>
                {errors[field.name]}
              </Typography>
            </Box>
          )}
          
          {/* Live Balance Metric */}
          {field.name === 'allocations' && paymentAmount > 0 && (
            <Box sx={{ 
              mb: 2, 
              p: 2, 
              backgroundColor: 'primary.50', 
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'primary.200'
            }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                📊 Ζωντανό Υπόλοιπο
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Συνολική Κατάθεση:
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                    €{paymentAmount.toFixed(2)}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Κατανεμημένο:
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                    €{totalAllocated.toFixed(2)}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Υπόλοιπο:
                  </Typography>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      fontWeight: 'bold',
                      color: displayRemaining >= 0 ? 'success.main' : 'error.main'
                    }}
                  >
                    €{displayRemaining.toFixed(2)}
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}
          
          {arrayValue.map((item, index) => (
            <Box
              key={index}
              sx={{
                p: 2,
                border: '1px solid',
                borderColor: 'grey.300',
                borderRadius: 2,
                mb: 2,
                backgroundColor: 'background.paper'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography variant="subtitle1">
                    {field.itemLabel || `Είδος ${index + 1}`}
                  </Typography>
                  {isServicePaid(item) && (
                    <Typography variant="caption" sx={{ color: 'success.main', fontWeight: 'bold' }}>
                      ✅ Εξοφλημένο
                    </Typography>
                  )}
                </Box>
                <IconButton
                  onClick={() => removeItem(index)}
                  color="error"
                  size="small"
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
              
              <Grid container spacing={2}>
                {field.itemSchema?.sections?.map((section, sectionIndex) => (
                  <React.Fragment key={sectionIndex}>
                    {section.fields?.map((itemField) => {
                      const fieldOptions = dynamicOptions[itemField.name] || itemField.options || [];
                      const isLoading = loadingOptions[itemField.name];
                      
                      const itemOnChange = (nameOrEvent, directValue) => {
                        let fieldName, fieldValue;
                        
                        if (typeof nameOrEvent === 'string') {
                          fieldName = nameOrEvent;
                          fieldValue = directValue;
                        } else {
                          const event = nameOrEvent;
                          fieldName = event.target.name;
                          fieldValue = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
                        }
                        
                        updateItem(index, { [fieldName]: fieldValue });
                      };
                      
                      return (
                        <Grid item xs={12} sm={itemField.size || 6} key={itemField.name}>
        <FormField
          {...itemField}
          name={itemField.name}
          value={item[itemField.name] || ''}
          onChange={itemOnChange}
          error={errors[`${field.name}.${index}.${itemField.name}`]}
          options={fieldOptions}
          loading={isLoading}
        />
                        </Grid>
                      );
                    })}
                  </React.Fragment>
                ))}
              </Grid>
            </Box>
          ))}
          
          {arrayValue.length === 0 && (
            <Box
              sx={{
                p: 3,
                border: '2px dashed',
                borderColor: 'grey.300',
                borderRadius: 2,
                textAlign: 'center',
                color: 'text.secondary'
              }}
            >
              <Typography variant="body2">
                Δεν υπάρχουν κατανομές. Κάντε κλικ στο "Προσθήκη" για να προσθέσετε.
              </Typography>
            </Box>
          )}
        </Box>
      </Grid>
    );
  };

  const renderSection = (section) => {
    // Skip section if conditional show returns false
    if (section.conditional && section.conditional.show) {
      if (!section.conditional.show(formData)) {
        return null;
      }
    }
    // Handle custom components
    if (section.customComponent === 'BillingInfoSection') {
      return (
        <Box key={section.title} sx={{ mb: 3 }}>
          {section.title && (
            <Box sx={{ mb: 2, pb: 1, borderBottom: '1px solid #e0e0e0' }}>
              <Box component="h3" sx={{ 
                margin: 0, 
                fontSize: '16px', 
                fontWeight: 600,
                color: 'text.primary'
              }}>
                {section.title}
              </Box>
            </Box>
          )}
          <BillingInfoSection
            clientId={entity?.id || null}
            billingInfoList={formData.billing_info || []}
            onChange={(updatedBillingInfo) => {
              setFormValue('billing_info', updatedBillingInfo);
            }}
            readOnly={false}
            errors={errors}
          />
        </Box>
      );
    }
    
    return (
      <Box key={section.title} sx={{ mb: 3 }}>
        {section.title && (
          <Box sx={{ mb: 2, pb: 1, borderBottom: '1px solid #e0e0e0' }}>
            <Box component="h3" sx={{ 
              margin: 0, 
              fontSize: '16px', 
              fontWeight: 600,
              color: 'text.primary'
            }}>
              {section.title}
            </Box>
          </Box>
        )}
        <Grid container spacing={2}>
          {section.fields && section.fields.map(renderField)}
        </Grid>
      </Box>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={modalProps.maxWidth || 'md'}
      fullWidth
      PaperProps={{
        sx: {
          minHeight: '400px',
          ...modalProps.sx
        }
      }}
    >
      <DialogTitle sx={{ 
        borderBottom: '1px solid #e0e0e0',
        fontWeight: 600
      }}>
        {modalProps.title || (entity ? 'Επεξεργασία' : 'Δημιουργία')}
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {entity ? 'Επιτυχής ενημέρωση!' : 'Επιτυχής δημιουργία!'}
          </Alert>
        )}

        {Object.keys(errors).length > 0 && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Παρακαλώ διορθώστε τα σφάλματα στη φόρμα
          </Alert>
        )}

        <Box component="form" onSubmit={(e) => { e.preventDefault(); handleFormSubmit(); }}>
          {schema.sections ? (
            schema.sections.map(renderSection)
          ) : (
            <Grid container spacing={2}>
              {schema.fields && schema.fields.map(renderField)}
            </Grid>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ 
        borderTop: '1px solid #e0e0e0',
        p: 2,
        gap: 1
      }}>
        <Button 
          onClick={onClose} 
          disabled={loading}
          variant="outlined"
        >
          Ακύρωση
        </Button>
        <Button 
          onClick={handleFormSubmit}
          disabled={loading}
          variant="contained"
          startIcon={loading && <CircularProgress size={16} />}
        >
          {loading ? 'Αποθήκευση...' : (entity ? 'Ενημέρωση' : 'Δημιουργία')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FormBuilder;